import { Timestamp } from 'firebase/firestore';
import { chain, find, get, head, isArray, map, set } from 'lodash';
import { mapActions, mapState } from 'pinia';
import { uid } from 'quasar';
import { notifyNegative } from 'src/composables/interactions';
import ChartFavoritesDB from 'src/fb/chartFavorites-db';
import GenericDb from 'src/fb/generic-db';
import ProgramsDB from 'src/fb/programs-db';
import ProgramTargetsDB from 'src/fb/programTargets-db';
import SessionsDB from 'src/fb/sessions-db';
import Targets from 'src/mixins/Targets';
import { programs as programsStore } from 'src/store/programs';
import { programsTargets } from 'src/store/programsTargets';
import { sessions } from 'src/store/sessions';
import { logEvent } from 'src/utils/LogEvent';

export default {
  mixins: [Targets],

  data() {
    return {
      automationActionOptions: [
        {
          value: 'session.continue',
          label: this.$t('editProgram.automationEditor.actions.session.continue'),
          valueType: 'none',
          valueLabel: null,
        },
        {
          value: 'program.setStatus',
          label: this.$t('editProgram.automationEditor.actions.program.setStatus'),
          valueType: 'select',
          valueLabel: this.$t('editProgram.automationEditor.actions.program.setStatusLabel'),
        },
        {
          value: 'program.setStatusIfWaiting',
          label: this.$t('editProgram.automationEditor.actions.program.setStatusIfWaiting'),
          valueType: 'select',
          valueLabel: this.$t('editProgram.automationEditor.actions.program.setStatusLabel'),
        },
        {
          value: 'program.setTargetStatusToWaiting',
          label: this.$t('editProgram.automationEditor.actions.program.setTargetStatusToWaiting'),
          valueType: 'select',
          valueLabel: this.$t('editProgram.automationEditor.actions.program.setTargetStatusLabel'),
        },
        {
          value: 'program.setTargetStatusToProbe',
          label: this.$t('editProgram.automationEditor.actions.program.setTargetStatusToProbe'),
          valueType: 'select',
          valueLabel: this.$t('editProgram.automationEditor.actions.program.setTargetStatusLabel'),
        },
        {
          value: 'program.setTargetStatusToAcquisition',
          label: this.$t('editProgram.automationEditor.actions.program.setTargetStatusToAcquisition'),
          valueType: 'select',
          valueLabel: this.$t('editProgram.automationEditor.actions.program.setTargetStatusLabel'),
        },
        {
          value: 'program.setTargetStatusToMastered',
          label: this.$t('editProgram.automationEditor.actions.program.setTargetStatusToMastered'),
          valueType: 'select',
          valueLabel: this.$t('editProgram.automationEditor.actions.program.setTargetStatusLabel'),
        },
        {
          value: 'program.setTargetStatusToClosed',
          label: this.$t('editProgram.automationEditor.actions.program.setTargetStatusToClosed'),
          valueType: 'select',
          valueLabel: this.$t('editProgram.automationEditor.actions.program.setTargetStatusLabel'),
        },
        {
          value: 'insights.addPhaseChange',
          label: this.$t('editProgram.automationEditor.actions.insights.addPhaseChange'),
          valueType: 'text',
          valueLabel: this.$t('editProgram.automationEditor.actions.insights.addPhaseChangeLabel'),
        },
        {
          value: 'session.navigate',
          label: this.$t('editProgram.automationEditor.actions.session.navigate'),
          valueType: 'none',
          valueLabel: null,
        },
        {
          value: 'session.finish',
          label: this.$t('editProgram.automationEditor.actions.session.finish'),
          valueType: 'none',
          valueLabel: null,
        },
      ],

    };
  },

  computed: {
    ...mapState(sessions, ['activeSession', 'activeSessionPrograms']),
  },

  methods: {
    ...mapActions(programsTargets, ['invalidateProgramTargetsCache']),
    ...mapActions(programsStore, ['updateProgramById']),

    getAutomationActionLabel(action) {
      return chain(this.automationActionOptions)
        .find({ value: action })
        .get('label')
        .value();
    }, // getAutomationActionLabel()

    async getAutomationParameterList(program, action) {
      switch (action) {
        case 'program.setStatus':
        case 'program.setStatusIfWaiting':
        case 'program.setTargetStatusToWaiting':
        case 'program.setTargetStatusToProbe':
        case 'program.setTargetStatusToAcquisition':
        case 'program.setTargetStatusToMastered':
        case 'program.setTargetStatusToClosed':
        {
          // Return list of programs
          const programsDb = new ProgramsDB();
          let programs;
          if (program.package) {
            // Return programs from same package
            programs = await programsDb.readAll([
              ['package', '==', program.package],
              ['client', '==', null],
              ['clinic', '==', null],
              ['isDeleted', '==', false],
            ]);
          } else {
            programs = await programsDb.readAll([
              ['client', '==', program.client],
              ['isDeleted', '==', false],
            ]);
          }
          return chain(programs)
            .map((p) => ({ value: p.programName, label: p.programName }))
            .orderBy('label')
            .value();
        }
        case 'insights.addPhaseChange': {
          // Return list of insights graphs
          const chartFavoritesDb = new ChartFavoritesDB();
          let chartFavorites;
          if (program.package) {
            // Return programs from same package
            chartFavorites = await chartFavoritesDb.readAll([['package', '==', program.package]]);
          } else {
            chartFavorites = await chartFavoritesDb.readAll([
              ['client', '==', program.client],
            ]);
          }
          return chain(chartFavorites)
            .map((cf) => ({ value: cf.favoriteName, label: cf.favoriteName }))
            .orderBy('label')
            .value();
        }
        case 'session.navigate': {
          return [
            { value: 'forward', label: this.$t('editProgram.automationEditor.actions.session.forward') },
            { value: 'back', label: this.$t('editProgram.automationEditor.actions.session.back') },
          ];
        }
        default:
          return [{ value: null, label: '' }];
      }
    }, // getAutomationParameterList

    async getAutomationValueList(program, action, parameter) {
      switch (action) {
        case 'program.setStatus':
        case 'program.setStatusIfWaiting': {
          return [
            { value: 'waiting', label: this.$t('programStatus_waiting') },
            { value: 'baseline', label: this.$t('programStatus_baseline') },
            { value: 'active', label: this.$t('programStatus_active') },
            { value: 'maintenance', label: this.$t('programStatus_maintenance') },
            { value: 'hold', label: this.$t('programStatus_hold') },
            { value: 'closed', label: this.$t('programStatus_closed') },
          ];
        }
        case 'program.setTargetStatusToWaiting':
        case 'program.setTargetStatusToProbe':
        case 'program.setTargetStatusToAcquisition':
        case 'program.setTargetStatusToMastered':
        case 'program.setTargetStatusToClosed': {
          // Get program
          const programsDb = new ProgramsDB();
          let programs;
          if (program.package) {
            // Return programs from same package
            programs = await programsDb.readAll([
              ['package', '==', program.package],
              ['programName', '==', parameter],
              ['client', '==', null],
              ['clinic', '==', null],
              ['isDeleted', '==', false],
            ]);
          } else {
            programs = await programsDb.readAll([
              ['client', '==', program.client],
              ['programName', '==', parameter],
              ['isDeleted', '==', false],
            ]);
          }
          const id = chain(programs).head().get('id').value();
          if (!id) {
            return [];
          }

          // Return list of programs
          const programTargetsDb = new ProgramTargetsDB(id);
          const programTargets = await programTargetsDb.readAll();
          return chain(programTargets)
            .map((pt) => ({ value: pt.label, label: pt.label }))
            .orderBy('label')
            .value();
        }
        default:
      }
      return [];
    }, // getAutomationValueList

    goToCurrentSessionProgram(sessionProgramId) {
      if (!this.activeSession?.currentProgram) {
        return;
      }
      const sessionsDb = new SessionsDB();
      return sessionsDb.set({ currentProgram: sessionProgramId }, this.activeSession.id);
    }, // goToCurrentSessionProgram()

    async getReadDataForProgramSetStatusAction(step, clientId, programName, transactionMethods) {
      const programsDb = new ProgramsDB();
      const programs = await transactionMethods.readAll(programsDb, {
        constraints: [
          ['client', '==', clientId],
          ['programName', '==', programName],
          ['isDeleted', '==', false],
        ],
      });

      const program = head(programs);
      const id = get(program, 'id');
      if (!id) {
        logEvent('Automation: no Program found for program setStatus action', {
          severity: 'warning',
          context: {
            context_data: {
              client: clientId,
              programName,
            },
          },
        });
        return step;
      }

      return {
        ...step,
        transactionReadData: {
          program,
          id,
        },
      };
    }, // getReadDataForProgramSetStatus()

    async runAutomationActionProgramSetStatus(step, clientId, transactionMethods, allowedStatuses = null) {
      const { parameter: programName, value: programStatus, transactionReadData } = step;
      try {
        const programsDb = new ProgramsDB();
        const isStatusForbidden = isArray(allowedStatuses)
          && !allowedStatuses.includes(transactionReadData?.program.status);

        if (!transactionReadData || isStatusForbidden) {
          return;
        }
        const { program, id } = transactionReadData;
        const now = Timestamp.now();
        const statusChanges = get(program, 'statusChanges', {});
        set(statusChanges, programStatus, now);
        await transactionMethods.set(
          programsDb,
          { data: { status: programStatus, statusChanges }, id },
        );

        // Go to program if we have just set it active
        let newCurrentSessionProgram = null;
        if (programStatus === 'active') {
          const activeSessionProgram = find(
            this.activeSessionPrograms,
            (sessionProgram) => (sessionProgram.program?.id === id),
          );
          if (activeSessionProgram?.id) {
            newCurrentSessionProgram = activeSessionProgram.id;
          }
        }

        return {
          programsWithUpdates: [{
            program: id,
            update: {
              status: programStatus,
              statusChanges,
            },
          }],
          currentSessionProgram: newCurrentSessionProgram,
        };
      } catch (err) {
        notifyNegative(this.$t('automation.notifyProgramNotFoundError', { parameter: programName }));
        throw err;
      }
    }, // runAutomationActionProgramSetStatus()

    async runAutomationActionProgramIdSetStatus(programId, programStatus, transactionMethods) {
      const programsDb = new ProgramsDB();
      try {
        const now = Timestamp.now();
        const programChanges = {
          id: programId,
          status: programStatus,
        };
        set(programChanges, `statusChanges.${programStatus}`, now);

        await transactionMethods.update(programsDb, { data: programChanges });

        return {
          program: programId,
          update: {
            status: programStatus,
          },
        };
      } catch (err) {
        notifyNegative(this.$t('automation.notifyProgramNotFoundError', { parameter: programId }));
        throw err;
      }
    }, // runAutomationActionProgramIdSetStatus()

    async getReadDataForProgramSetTargetStatusAction(step, clientId, transactionMethods) {
      const { parameter: programName, value: targetName } = step;

      const programsDb = new ProgramsDB();
      const programs = await transactionMethods.readAll(programsDb, {
        constraints: [
          ['client', '==', clientId],
          ['programName', '==', programName],
          ['isDeleted', '==', false],
        ],
      });

      const program = head(programs);
      const programId = program?.id;

      if (!programId) {
        logEvent('Automation: no Program found for target setStatus action', {
          severity: 'warning',
          context: {
            context_data: {
              client: clientId,
              programName,
            },
          },
        });
        return step;
      }

      const programTargetsDb = new ProgramTargetsDB(programId);

      const programTargets = await transactionMethods.readAll(programTargetsDb);
      const programTargetsWithSameLabel = programTargets.filter((programTarget) => programTarget.label === targetName);

      const programTargetId = chain(programTargetsWithSameLabel).head().get('id').value();

      if (!programTargetId) {
        logEvent('Automation: no Target found for target setStatus action', {
          severity: 'warning',
          context: {
            context_data: {
              programId,
              targetName,
            },
          },
        });
        return step;
      }

      return {
        ...step,
        transactionReadData: {
          programTargets,
          programTargetId,
          program,
        },
      };
    }, // getReadDataForProgramSetStatus()

    async runAutomationActionProgramSetTargetStatus(step, clientId, targetStatus, transactionMethods) {
      const { value: targetName, transactionReadData } = step;

      if (!transactionReadData) {
        return;
      }

      const { program, programTargets, programTargetId } = transactionReadData;
      const programTargetsDb = new ProgramTargetsDB(program.id);

      try {
        transactionMethods.set(programTargetsDb, { data: { status: targetStatus }, id: programTargetId });
        await this.updateTargetsPerStatusWithTransaction(program, programTargets, transactionMethods);
        return {
          programForCacheInvalidation: program.id,
        };
      } catch (err) {
        notifyNegative(this.$t('automation.notifyTargetNotFoundError', { value: targetName }));
        throw err;
      }
    }, // runAutomationActionProgramSetTargetStatus()

    async getReadDataForInsightsAddPhaseChangeAction(step, clientId, transactionMethods) {
      const { parameter: chartFavoriteName } = step;
      const chartFavoritesDb = new ChartFavoritesDB();
      const chartFavorites = await transactionMethods.readAll(chartFavoritesDb, {
        constraints: [
          ['client', '==', clientId],
          ['favoriteName', '==', chartFavoriteName],
        ],
      });

      const chartFavorite = head(chartFavorites);
      const dataSeriesSettings = get(chartFavorite, 'dataSeriesSettings');
      if (!dataSeriesSettings) {
        logEvent('Automation: no DataSeriesSettings found for chart addPhaseChange action', {
          severity: 'warning',
          context: {
            context_data: {
              client: clientId,
              favoriteName: chartFavoriteName,
            },
          },
        });
        return step;
      }

      return {
        ...step,
        transactionReadData: {
          chartFavoriteId: chartFavorite.id,
          dataSeriesSettings,
        },
      };
    }, // getReadDataForProgramSetStatus()

    async runAutomationActionInsightsAddPhaseChange(step, clientId, transactionMethods) {
      const { parameter: chartFavoriteName, value: phaseChangeLabel, transactionReadData } = step;
      try {
        const chartFavoritesDb = new ChartFavoritesDB();

        if (!transactionReadData) {
          return;
        }

        const { chartFavoriteId, dataSeriesSettings } = transactionReadData;

        const phaseLines = get(dataSeriesSettings, 'phaseLines', []);
        phaseLines.push({
          id: uid(),
          label: phaseChangeLabel,
          startTimestamp: Timestamp.now(),
        });
        set(dataSeriesSettings, 'phaseLines', phaseLines);

        await transactionMethods.set(chartFavoritesDb, { data: { dataSeriesSettings }, id: chartFavoriteId });
      } catch (err) {
        notifyNegative(this.$t('automation.notifyChartFavoriteNotFoundError', { parameter: chartFavoriteName }));
        throw err;
      }
    }, // runAutomationActionInsightsAddPhaseChange()

    async runAutomationActionNavigate(direction) {
      const currentSessionProgram = find(this.activeSessionPrograms, { id: this.activeSession?.currentProgram });
      if (!currentSessionProgram) {
        return;
      }

      if (direction === 'forward') {
        // Find next sessionProgram
        const nextSessionProgram = chain(this.activeSessionPrograms)
          .reject((sessionProgram) => sessionProgram.order <= currentSessionProgram.order)
          .head()
          .value();
        if (nextSessionProgram) {
          return {
            currentSessionProgram: nextSessionProgram.id,
          };
        }
      }
      // Find previous sessionProgram
      const previousSessionProgram = chain(this.activeSessionPrograms)
        .reject((sessionProgram) => sessionProgram.order >= currentSessionProgram.order)
        .last()
        .value();
      if (previousSessionProgram) {
        return {
          currentSessionProgram: previousSessionProgram.id,
        };
      }
    }, // runAutomationActionNavigate()

    async runAutomationActionContinueSession(transactionMethods) {
      const currentSessionProgram = find(this.activeSessionPrograms, { id: this.activeSession?.currentProgram });
      if (!currentSessionProgram) {
        return;
      }

      const changedPrograms = [];

      if (currentSessionProgram.program.status === 'active') {
        const currentProgramWithUpdate = await this.runAutomationActionProgramIdSetStatus(
          currentSessionProgram.program.id,
          'closed',
          transactionMethods,
        );
        changedPrograms.push(currentProgramWithUpdate);
      }

      // Find next sessionProgram
      const nextSessionProgram = chain(this.activeSessionPrograms)
        .reject((sessionProgram) => sessionProgram.order <= currentSessionProgram.order)
        .head()
        .value();

      if (!nextSessionProgram) {
        return;
      }
      const nextProgram = get(nextSessionProgram, 'program');
      if (nextProgram.status === 'waiting') {
        const nextProgramWithUpdate = await this.runAutomationActionProgramIdSetStatus(
          nextProgram.id,
          'active',
          transactionMethods,
        );
        changedPrograms.push(nextProgramWithUpdate);
      }

      return {
        programsWithUpdates: changedPrograms,
        currentSessionProgram: nextSessionProgram.id,
      };
    },

    async runAutomationActionFinishSession(transactionMethods) {
      const activePrograms = chain(this.activeSessionPrograms)
        .map('program')
        .filter({ status: 'active' })
        .map('id')
        .value();

      const closeProgramsActions = map(
        activePrograms,
        (programId) => this.runAutomationActionProgramIdSetStatus(programId, 'closed', transactionMethods),
      );
      const changedPrograms = await Promise.all(closeProgramsActions);

      return {
        programsWithUpdates: changedPrograms,
        finishSession: true,
      };
    }, // runAutomationActionFinishSession()

    async runAutomationAction(clientId, programAutomationAction) {
      const genericDb = new GenericDb();

      const stepActions = await genericDb.executeTransaction(async (transactionMethods) => {
        const programAutomationActionWithReadData = await Promise.all(
          programAutomationAction.steps.map(async (step) => {
            return this.prepareReadDataForAutomationStep(step, clientId, transactionMethods);
          }),
        );

        return Promise.all(chain(programAutomationActionWithReadData)
          .map(async (step) => {
            return this.handleAutomationStep(step, clientId, transactionMethods);
          })
          .value());
      });

      this.updateStoreByStepActions(stepActions);

      return chain(stepActions)
        .compact()
        .reduce((pV, cV) => {
          const currentPrograms = cV.programsWithUpdates?.map((programWithUpdate) => programWithUpdate.program) || [];
          return {
            programs: [...pV.programs, ...currentPrograms],
            currentSessionProgram: cV.currentSessionProgram || pV.currentSessionProgram,
            finishSession: cV.finishSession || pV.finishSession,
          };
        }, { programs: [], currentSessionProgram: null, finishSession: false })
        .value();
    }, // runAutomationAction()

    // moved all necessary changes for vuex store to this function to run it after transaction
    // added programsWithUpdate to encapsulate program and it's update that should be dispatched to vuex store
    updateStoreByStepActions(stepActions) {
      stepActions?.forEach((step) => {
        if (step.programsWithUpdates) {
          step.programsWithUpdates.forEach((programWithUpdate) => {
            if (programWithUpdate.update) {
              this.updateProgramById({ id: programWithUpdate.program, programUpdates: programWithUpdate.update });
            }
          });
        }
        if (step?.programForCacheInvalidation) {
          this.invalidateProgramTargetsCache({ program: step.programForCacheInvalidation });
        }
      });
    }, // updateStoreByStepActions()

    async prepareReadDataForAutomationStep(step, clientId, transactionMethods) {
      switch (step.action) {
        case 'program.setStatus':
        case 'program.setStatusIfWaiting': {
          return this.getReadDataForProgramSetStatusAction(step, clientId, step.parameter, transactionMethods);
        }
        case 'program.setTargetStatusToWaiting':
        case 'program.setTargetStatusToProbe':
        case 'program.setTargetStatusToAcquisition':
        case 'program.setTargetStatusToMastered':
        case 'program.setTargetStatusToClosed': {
          return this.getReadDataForProgramSetTargetStatusAction(step, clientId, transactionMethods);
        }
        case 'insights.addPhaseChange': {
          return this.getReadDataForInsightsAddPhaseChangeAction(step, clientId, transactionMethods);
        }
        default:
      }
      return step;
    }, // prepareReadDataForAutomationStep()

    async handleAutomationStep(step, clientId, transactionMethods) {
      switch (step.action) {
        case 'program.setStatus': {
          return this.runAutomationActionProgramSetStatus(step, clientId, transactionMethods);
        }
        case 'program.setStatusIfWaiting': {
          return this.runAutomationActionProgramSetStatus(step, clientId, transactionMethods, ['waiting']);
        }
        case 'program.setTargetStatusToWaiting': {
          return this.runAutomationActionProgramSetTargetStatus(step, clientId, 'waiting', transactionMethods);
        }
        case 'program.setTargetStatusToProbe': {
          return this.runAutomationActionProgramSetTargetStatus(step, clientId, 'probe', transactionMethods);
        }
        case 'program.setTargetStatusToAcquisition': {
          return this.runAutomationActionProgramSetTargetStatus(step, clientId, 'acquisition', transactionMethods);
        }
        case 'program.setTargetStatusToMastered': {
          return this.runAutomationActionProgramSetTargetStatus(step, clientId, 'mastered', transactionMethods);
        }
        case 'program.setTargetStatusToClosed': {
          return this.runAutomationActionProgramSetTargetStatus(step, clientId, 'closed', transactionMethods);
        }
        case 'insights.addPhaseChange': {
          return this.runAutomationActionInsightsAddPhaseChange(step, clientId, transactionMethods);
        }
        case 'session.navigate': {
          return this.runAutomationActionNavigate(step.parameter);
        }
        case 'session.continue': {
          return this.runAutomationActionContinueSession(transactionMethods);
        }
        case 'session.finish': {
          return this.runAutomationActionFinishSession(transactionMethods);
        }
        default:
      }
      return null;
    }, // handleAutomationStep()

  },
};
